/**
 * Pieces the partner Billing screen and the operator billing panel share:
 * the ledger kinds' names, the invoice status chip, a signed amount and the
 * statement table (cards on a phone, like the leads inbox).
 */
import type { ReactNode } from 'react';
import type { InvoiceStatus, InvoiceView, LedgerEntryView, LedgerKind } from '../api/types';
import { Link } from '../router';
import { NotSet, formatDate, formatDateTime } from '../ui/kit';
import { usePhone } from '../ui/hooks';
import { formatCents } from './money';

export const LEDGER_KINDS: readonly LedgerKind[] = ['lead_fee', 'success_fee', 'credit', 'adjustment', 'payment'];

export const LEDGER_KIND_LABEL: Record<LedgerKind, string> = {
  lead_fee: 'Lead fee',
  success_fee: 'Success fee',
  credit: 'Credit',
  adjustment: 'Adjustment',
  payment: 'Payment',
};

export function ledgerKindLabel(kind: string): string {
  return (LEDGER_KIND_LABEL as Record<string, string | undefined>)[kind] ?? kind;
}

const INVOICE_STATUS: Record<InvoiceStatus, { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: '' },
  issued: { label: 'Issued', tone: 'warn' },
  paid: { label: 'Paid', tone: 'ok' },
  void: { label: 'Void', tone: '' },
};

export function InvoiceStatusTag({ status }: { status: InvoiceStatus }) {
  const s = INVOICE_STATUS[status] ?? { label: status, tone: '' };
  return <span className={`tag ${s.tone}`.trim()}>{s.label}</span>;
}

/** "FX-2026-000123", or "Draft" before it is issued. */
export function invoiceName(inv: Pick<InvoiceView, 'number'>): string {
  return inv.number ?? 'Draft invoice';
}

/** "Oct 1 – Oct 31, 2026". */
export function periodLabel(inv: Pick<InvoiceView, 'period_start' | 'period_end'>): string {
  return `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`;
}

/** A ledger amount: positive owed, negative a credit or payment (with the label a screen reader needs). */
export function Amount({ cents }: { cents: number }) {
  return (
    <span className="nowrap" style={{ fontVariantNumeric: 'tabular-nums', color: cents < 0 ? 'var(--ok)' : undefined }}>
      {formatCents(cents)}
      {cents < 0 ? <span className="visually-hidden"> (credit)</span> : null}
    </span>
  );
}

/** Where an entry came from: its lead (`/leads/:id`), else its project's id. */
function EntrySource({ entry, linkLeads }: { entry: LedgerEntryView; linkLeads: boolean }) {
  if (entry.lead_id) {
    return linkLeads ? (
      <Link to={`/leads/${encodeURIComponent(entry.lead_id)}`}>
        {entry.project_id ? 'Job' : 'Lead'}
        <span className="visually-hidden"> {entry.lead_id}</span>
      </Link>
    ) : (
      <code className="ops-code wrap-anywhere">{entry.lead_id}</code>
    );
  }
  if (entry.project_id) return <code className="ops-code wrap-anywhere">{entry.project_id}</code>;
  return <NotSet label="None" />;
}

/**
 * The statement lines. `linkLeads`: the partner's own leads open in the
 * inbox; the operator console shows the ids instead (the inbox is the
 * operator's own organisation). `actions` adds a last column per row.
 */
export function LedgerTable({
  entries,
  caption,
  linkLeads = true,
  actions,
}: {
  entries: LedgerEntryView[];
  caption: string;
  linkLeads?: boolean;
  actions?: (entry: LedgerEntryView) => ReactNode;
}) {
  const phone = usePhone();
  if (phone) {
    return (
      <ul className="cards" aria-label={caption} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {entries.map((e) => (
          <li key={e.id} className="card">
            <span className="row-between">
              <strong>{ledgerKindLabel(e.kind)}</strong>
              <Amount cents={e.amount_cents} />
            </span>
            <span>{e.description || <NotSet label="No description" />}</span>
            <span className="small muted row" style={{ gap: 8 }}>
              <time dateTime={e.posted_at} title={formatDateTime(e.posted_at)}>
                {formatDate(e.posted_at)}
              </time>
              {e.lead_id || e.project_id ? <EntrySource entry={e} linkLeads={linkLeads} /> : null}
            </span>
            {actions ? <span>{actions(e)}</span> : null}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Kind</th>
            <th scope="col">Description</th>
            <th scope="col">Lead</th>
            <th scope="col" className="num">
              Amount
            </th>
            {actions ? (
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="nowrap">
                <time dateTime={e.posted_at} title={formatDateTime(e.posted_at)}>
                  {formatDate(e.posted_at)}
                </time>
              </td>
              <td className="nowrap">{ledgerKindLabel(e.kind)}</td>
              <td>{e.description || <NotSet label="No description" />}</td>
              <td>
                <EntrySource entry={e} linkLeads={linkLeads} />
              </td>
              <td className="num">
                <Amount cents={e.amount_cents} />
              </td>
              {actions ? <td className="nowrap">{actions(e)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
